// ============================================================
// PILLAR 5: Security & 2FA UI Components
// MedSpaBookings OS - Production Security Interface
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, ShieldAlert, Lock, Download, Copy, X, CheckCircle2, AlertCircle, Monitor, Smartphone, Globe, FileText } from 'lucide-react';

// ============================================================
// 2FA Enrollment Component
// ============================================================

export function TwoFactorEnrollment({ api, user, showNotice, onComplete }) {
  // A user who already has 2FA enabled (user.two_factor_enabled, present
  // on the team doc returned by /auth/me and /auth/login) starts on the
  // "manage" step instead of being walked through enrollment again.
  const [step, setStep] = useState(user.two_factor_enabled ? 'manage' : 'initial'); // manage, initial, enrolled, verified
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disabling, setDisabling] = useState(false);

  const enrollTwoFactor = async () => {
    setLoading(true);
    try {
      const res = await api.post('/auth/2fa/enroll');
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setRecoveryCodes(res.data.recovery_codes);
      setStep('enrolled');
      showNotice('Scan QR code with your authenticator app', 'info');
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Enrollment failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const verifyEnrollment = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      showNotice('Enter 6-digit verification code', 'error');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/2fa/verify-enrollment', { token: verificationCode });
      setStep('verified');
      showNotice('2FA enabled successfully!', 'success');
      if (onComplete) onComplete();
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Invalid verification code', 'error');
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    if (!disablePassword) {
      showNotice('Enter your password to disable 2FA', 'error');
      return;
    }
    setDisabling(true);
    try {
      await api.post('/auth/2fa/disable', { password: disablePassword });
      showNotice('2FA disabled', 'success');
      window.location.reload();
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to disable 2FA', 'error');
    } finally {
      setDisabling(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotice('Copied to clipboard', 'success');
  };

  const downloadRecoveryCodes = () => {
    const text = `MedSpaBookings Recovery Codes\nUser: ${user.email}\nGenerated: ${new Date().toLocaleString()}\n\n${recoveryCodes.join('\n')}\n\nStore these codes in a safe place. Each code can only be used once.`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `medspabookings-recovery-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    showNotice('Recovery codes downloaded', 'success');
  };

  if (step === 'manage') {
    return (
      <div className="panel glass-panel" data-testid="2fa-manage-panel">
        <div className="panel-header">
          <h3>Two-Factor Authentication</h3>
          <span>Your account is protected with 2FA</span>
        </div>

        <div style={{ textAlign: 'center', padding: '20px' }}>
          <ShieldCheck size={48} color="var(--success)" style={{ marginBottom: '12px' }} />
          <p style={{ color: 'var(--muted-foreground)' }}>
            Two-factor authentication is currently enabled on your account.
          </p>
        </div>

        {user.role === 'Master Administrator' ? (
          <div style={{
            padding: '12px',
            background: 'rgba(212,166,42,0.1)',
            border: '1px solid rgba(212,166,42,0.3)',
            borderRadius: '8px'
          }}>
            <strong>Master Administrator Protection:</strong> 2FA is mandatory for your role and cannot be disabled.
          </div>
        ) : (
          <div style={{ marginTop: '16px' }}>
            <h4 style={{ marginBottom: '8px' }}>Disable 2FA</h4>
            <label>
              Confirm your password
              <input
                data-testid="2fa-disable-password-input"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
              />
            </label>
            <button
              className="danger-btn"
              data-testid="2fa-disable-button"
              onClick={disable2FA}
              disabled={disabling || !disablePassword}
              style={{ marginTop: '12px' }}
            >
              {disabling ? 'Disabling...' : 'Disable 2FA'}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (step === 'verified') {
    return (
      <div className="panel glass-panel" data-testid="2fa-success-panel">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <CheckCircle2 size={64} color="var(--success)" style={{ marginBottom: '20px' }} />
          <h2>2FA Enabled Successfully</h2>
          <p style={{ color: 'var(--muted-foreground)', marginBottom: '24px' }}>
            Your account is now protected with two-factor authentication.
          </p>
          <button className="primary-btn" onClick={() => window.location.reload()}>
            Done
          </button>
        </div>
      </div>
    );
  }

  if (step === 'enrolled') {
    return (
      <div className="panel glass-panel" data-testid="2fa-enrollment-panel">
        <div className="panel-header">
          <h3>Enable Two-Factor Authentication</h3>
          <span>Step 2: Scan QR code and verify</span>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '16px' }}>1. Scan QR Code</h4>
          {qrCode && (
            <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', display: 'inline-block' }}>
              <img src={qrCode} alt="2FA QR Code" style={{ width: '200px', height: '200px' }} />
            </div>
          )}
          <p style={{ marginTop: '12px', fontSize: '12px', color: 'var(--muted-foreground)' }}>
            Use Google Authenticator, Microsoft Authenticator, or any TOTP app
          </p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '8px' }}>Or enter this secret manually:</h4>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              value={secret}
              readOnly
              style={{ flex: 1, fontFamily: 'monospace', fontSize: '14px' }}
            />
            <button className="ghost-btn small" onClick={() => copyToClipboard(secret)}>
              <Copy size={14} /> Copy
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '16px' }}>2. Save Recovery Codes</h4>
          <div className="recovery-codes-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px',
            marginBottom: '12px',
            background: 'rgba(0,0,0,0.2)',
            padding: '16px',
            borderRadius: '8px'
          }}>
            {recoveryCodes.map((code, idx) => (
              <div key={idx} style={{
                fontFamily: 'monospace',
                fontSize: '13px',
                padding: '6px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '4px',
                textAlign: 'center'
              }}>
                {code}
              </div>
            ))}
          </div>
          <div className="button-row">
            <button className="ghost-btn small" onClick={downloadRecoveryCodes}>
              <Download size={14} /> Download Codes
            </button>
            <button className="ghost-btn small" onClick={() => copyToClipboard(recoveryCodes.join('\n'))}>
              <Copy size={14} /> Copy All
            </button>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--warning)', marginTop: '12px' }}>
            <AlertCircle size={14} style={{ verticalAlign: 'middle' }} /> Store these codes safely. Each can only be used once.
          </p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '8px' }}>3. Verify Setup</h4>
          <label>
            Enter 6-digit code from your authenticator app:
            <input
              data-testid="2fa-verification-input"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength="6"
              style={{ fontFamily: 'monospace', fontSize: '18px', letterSpacing: '4px', textAlign: 'center' }}
            />
          </label>
        </div>

        <button
          className="primary-btn"
          data-testid="2fa-verify-button"
          onClick={verifyEnrollment}
          disabled={loading || verificationCode.length !== 6}
        >
          {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
        </button>
      </div>
    );
  }

  return (
    <div className="panel glass-panel" data-testid="2fa-initial-panel">
      <div className="panel-header">
        <h3>Enable Two-Factor Authentication</h3>
        <span>Add an extra layer of security to your account</span>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h4>What is 2FA?</h4>
        <p style={{ color: 'var(--muted-foreground)', lineHeight: '1.6' }}>
          Two-factor authentication (2FA) adds an additional layer of security by requiring a verification code from your phone in addition to your password.
        </p>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h4>You'll need:</h4>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
          <li>A smartphone with Google Authenticator or Microsoft Authenticator installed</li>
          <li>Access to your email for recovery codes</li>
        </ul>
      </div>

      {user.role === 'Master Administrator' && (
        <div style={{
          padding: '12px',
          background: 'rgba(212,166,42,0.1)',
          border: '1px solid rgba(212,166,42,0.3)',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <strong>Master Administrator Protection:</strong> 2FA is mandatory for your role and cannot be disabled.
        </div>
      )}

      <button
        className="primary-btn"
        data-testid="2fa-start-enrollment-button"
        onClick={enrollTwoFactor}
        disabled={loading}
      >
        <ShieldCheck size={16} /> {loading ? 'Setting up...' : 'Start 2FA Setup'}
      </button>
    </div>
  );
}

// ============================================================
// 2FA Login Verification Component
// ============================================================

export function TwoFactorVerification({ tempToken, onSuccess, onError, showNotice }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const verifyCode = async () => {
    if (!code || (useRecoveryCode ? code.length < 8 : code.length !== 6)) {
      showNotice(`Enter valid ${useRecoveryCode ? 'recovery code' : '6-digit code'}`, 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, totp_token: code })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Verification failed');
      }

      const data = await response.json();
      
      if (data.recovery_code_used) {
        showNotice('Recovery code used successfully. Please generate new codes.', 'warning');
      }
      
      onSuccess(data);
    } catch (err) {
      showNotice(err.message || 'Invalid verification code', 'error');
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-panel glass-panel" data-testid="2fa-verification-panel">
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <Smartphone size={48} color="var(--primary)" style={{ marginBottom: '12px' }} />
        <h2>Two-Factor Authentication</h2>
        <p style={{ color: 'var(--muted-foreground)' }}>
          {useRecoveryCode ? 'Enter your recovery code' : 'Enter the 6-digit code from your authenticator app'}
        </p>
      </div>

      <label>
        {useRecoveryCode ? 'Recovery Code' : 'Verification Code'}
        <input
          data-testid="2fa-code-input"
          type="text"
          value={code}
          onChange={(e) => setCode(useRecoveryCode ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder={useRecoveryCode ? 'XXXXXXXX' : '000000'}
          maxLength={useRecoveryCode ? 16 : 6}
          style={{
            fontFamily: 'monospace',
            fontSize: useRecoveryCode ? '16px' : '24px',
            letterSpacing: useRecoveryCode ? '2px' : '6px',
            textAlign: 'center'
          }}
          autoFocus
        />
      </label>

      <button
        className="primary-btn"
        data-testid="2fa-verify-login-button"
        onClick={verifyCode}
        disabled={loading || (!code || (useRecoveryCode ? code.length < 8 : code.length !== 6))}
      >
        {loading ? 'Verifying...' : 'Verify & Continue'}
      </button>

      <button
        className="ghost-btn"
        onClick={() => {
          setUseRecoveryCode(!useRecoveryCode);
          setCode('');
        }}
        style={{ marginTop: '12px' }}
      >
        {useRecoveryCode ? 'Use Authenticator Code' : 'Use Recovery Code Instead'}
      </button>
    </div>
  );
}

// ============================================================
// Security Dashboard Component (Master Admin Only)
// ============================================================

export function SecurityDashboard({ api, user, showNotice }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/security/dashboard');
      setDashboard(res.data);
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to load security dashboard', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (user.role !== 'Master Administrator') {
    return (
      <div className="panel glass-panel">
        <h2>Security Dashboard is Master Administrator only</h2>
      </div>
    );
  }

  if (loading || !dashboard) {
    return (
      <div className="panel glass-panel">
        <h3>Loading Security Dashboard...</h3>
      </div>
    );
  }

  return (
    <div className="page-stack" data-testid="security-dashboard">
      <section className="hero-panel ceo-hero glass-panel">
        <div>
          <p className="eyebrow">Security Control Center • Master Admin Only • PILLAR 5</p>
          <h2>Security Dashboard</h2>
          <div className="hero-stats">
            <span>2FA Monitoring</span>
            <span>Session Tracking</span>
            <span>Threat Detection</span>
            <span>Audit Logging</span>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="revenue-stat-grid">
        <div className="stat-tile glass-panel">
          <span>Failed Logins (24h)</span>
          <strong style={{ color: dashboard.failed_logins_24h > 5 ? 'var(--destructive)' : 'inherit' }}>
            {dashboard.failed_logins_24h}
          </strong>
        </div>
        <div className="stat-tile glass-panel">
          <span>Active Sessions</span>
          <strong>{dashboard.active_sessions}</strong>
        </div>
        <div className="stat-tile glass-panel">
          <span>Rate Limit Events (24h)</span>
          <strong>{dashboard.rate_limit_events_24h}</strong>
        </div>
        <div className="stat-tile glass-panel">
          <span>Suspicious Logins (7d)</span>
          <strong style={{ color: dashboard.suspicious_logins_7d > 0 ? 'var(--warning)' : 'inherit' }}>
            {dashboard.suspicious_logins_7d}
          </strong>
        </div>
      </section>

      {/* 2FA Status by User */}
      <section className="panel glass-panel">
        <div className="panel-header">
          <h3>2FA Status by User</h3>
          <span>{dashboard['2fa_status'].length} team members</span>
        </div>

        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>2FA Status</th>
              <th>Required</th>
            </tr>
          </thead>
          <tbody>
            {dashboard['2fa_status'].map((member) => (
              <tr key={member.user_id}>
                <td>{member.name}</td>
                <td>{member.email}</td>
                <td>{member.role}</td>
                <td>
                  {member['2fa_enabled'] ? (
                    <span style={{ color: 'var(--success)' }}>
                      <CheckCircle2 size={16} style={{ verticalAlign: 'middle' }} /> Enabled
                    </span>
                  ) : (
                    <span style={{ color: 'var(--muted-foreground)' }}>
                      <AlertCircle size={16} style={{ verticalAlign: 'middle' }} /> Disabled
                    </span>
                  )}
                </td>
                <td>{member['2fa_required'] ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Recent Security Activity */}
      <section className="panel glass-panel">
        <div className="panel-header">
          <h3>Recent Security Activity</h3>
          <span>Last {dashboard.recent_activity.length} events</span>
        </div>

        <div className="compact-list" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {dashboard.recent_activity.map((event) => (
            <div key={event.id} className="compact-row">
              {event.severity === 'warning' ? (
                <AlertCircle size={16} color="var(--warning)" />
              ) : event.severity === 'error' ? (
                <ShieldAlert size={16} color="var(--destructive)" />
              ) : (
                <ShieldCheck size={16} color="var(--success)" />
              )}
              <div>
                <strong>{event.event_type.replace(/_/g, ' ').toUpperCase()}</strong>
                <span>
                  {event.user_email || 'Unknown'} • {event.ip || 'No IP'} • {new Date(event.created_at).toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ============================================================
// Session Management Component
// ============================================================

export function SessionManagement({ api, showNotice }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const res = await api.get('/security/sessions');
      setSessions(res.data.sessions);
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to load sessions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const revokeSession = async (sessionId) => {
    try {
      await api.post(`/security/sessions/${sessionId}/revoke`);
      showNotice('Session revoked successfully', 'success');
      await loadSessions();
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to revoke session', 'error');
    }
  };

  if (loading) {
    return <div className="panel glass-panel"><h3>Loading sessions...</h3></div>;
  }

  return (
    <div className="panel glass-panel" data-testid="session-management-panel">
      <div className="panel-header">
        <h3>Active Sessions</h3>
        <span>{sessions.length} active</span>
      </div>

      <div className="compact-list">
        {sessions.map((session) => (
          <div key={session.id} className="compact-row" style={{ alignItems: 'flex-start' }}>
            <Monitor size={20} />
            <div style={{ flex: 1 }}>
              <strong>
                <Globe size={14} style={{ verticalAlign: 'middle' }} /> {session.ip || 'Unknown IP'}
              </strong>
              <span style={{ display: 'block', fontSize: '12px', marginTop: '4px' }}>
                {session.user_agent || 'Unknown browser'}
              </span>
              <span style={{ display: 'block', fontSize: '11px', color: 'var(--muted-foreground)', marginTop: '4px' }}>
                Login: {new Date(session.created_at).toLocaleString()} • Last activity: {new Date(session.last_activity).toLocaleString()}
              </span>
            </div>
            <button
              className="danger-btn small"
              onClick={() => revokeSession(session.id)}
              data-testid={`revoke-session-${session.id}`}
            >
              Revoke
            </button>
          </div>
        ))}
        {!sessions.length && (
          <div className="empty-cell">No active sessions</div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Audit Log Panel (Master Admin Only)
// ============================================================
// Backs onto GET /admin/audit-logs, /admin/audit-logs/export and
// /admin/audit-logs/stats - all Master-Admin-only, all already wired up
// server-side, but with no UI anywhere to view them until now.
//
// The only 3 call sites of log_audit_event() in server.py write
// event_type "admin_login_failed", "client_login_failed" and
// "backup_created" (successful logins go to a separate login_history
// collection this panel doesn't query) - the filter options below must
// match those exact stored strings, or GET /admin/audit-logs' exact-match
// event_type filter always returns zero results for that option.
const AUDIT_EVENT_TYPES = ['admin_login_failed', 'client_login_failed', 'backup_created'];
const AUDIT_STATUSES = ['success', 'failed'];

export function AuditLogPanel({ api, showNotice }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({ event_type: '', user_email: '', status: '' });
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (filters.event_type) params.event_type = filters.event_type;
      if (filters.user_email) params.user_email = filters.user_email;
      if (filters.status) params.status = filters.status;
      const [logsRes, statsRes] = await Promise.all([
        api.get('/admin/audit-logs', { params }),
        api.get('/admin/audit-logs/stats'),
      ]);
      setLogs(logsRes.data.logs || []);
      setTotal(logsRes.data.total || 0);
      setStats(statsRes.data);
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to load audit logs', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, showNotice, filters, offset]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = (next) => { setOffset(0); setFilters(next); };

  const exportLogs = async (format) => {
    setExporting(true);
    try {
      const params = { format };
      if (filters.event_type) params.event_type = filters.event_type;
      const res = await api.get('/admin/audit-logs/export', { params });
      const mime = format === 'csv' ? 'text/csv' : 'application/json';
      const content = format === 'csv' ? res.data.data : JSON.stringify(res.data.data, null, 2);
      const blob = new Blob([content], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-export-${Date.now()}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      showNotice('Audit log exported', 'success');
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page-stack" data-testid="audit-log-panel">
      <section className="revenue-stat-grid">
        <div className="stat-tile glass-panel">
          <span>Total Events</span>
          <strong>{stats?.total ?? 0}</strong>
        </div>
        <div className="stat-tile glass-panel">
          <span>Failed Events</span>
          <strong style={{ color: stats?.failed_count > 0 ? 'var(--destructive)' : 'inherit' }}>
            {stats?.failed_count ?? 0}
          </strong>
        </div>
        <div className="stat-tile glass-panel">
          <span>Event Types Tracked</span>
          <strong>{stats?.event_counts?.length ?? 0}</strong>
        </div>
      </section>

      <section className="panel glass-panel">
        <div className="panel-header">
          <h3>Audit Log</h3>
          <span>{total} matching events</span>
        </div>

        <div className="advanced-filters glass-panel" data-testid="audit-log-filters">
          <select
            data-testid="audit-log-filter-event-type"
            value={filters.event_type}
            onChange={(e) => applyFilters({ ...filters, event_type: e.target.value })}
          >
            <option value="">All Event Types</option>
            {AUDIT_EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            data-testid="audit-log-filter-status"
            value={filters.status}
            onChange={(e) => applyFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            {AUDIT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            data-testid="audit-log-filter-user-email"
            placeholder="User Email"
            value={filters.user_email}
            onChange={(e) => applyFilters({ ...filters, user_email: e.target.value })}
          />
          <button className="ghost-btn small" data-testid="audit-log-export-json-button" onClick={() => exportLogs('json')} disabled={exporting}>
            <Download size={14} /> Export JSON
          </button>
          <button className="ghost-btn small" data-testid="audit-log-export-csv-button" onClick={() => exportLogs('csv')} disabled={exporting}>
            <Download size={14} /> Export CSV
          </button>
        </div>

        {loading ? (
          <div className="empty-cell">Loading audit log...</div>
        ) : (
          <div className="compact-list">
            {logs.map((log) => (
              <div key={log.id} className="compact-row">
                {log.status === 'failed' ? (
                  <ShieldAlert size={16} color="var(--destructive)" />
                ) : (
                  <FileText size={16} />
                )}
                <div>
                  <strong>{String(log.event_type || '').replace(/_/g, ' ').toUpperCase()}</strong>
                  <span>
                    {log.user_email || 'Unknown'} • {log.status || 'unknown'} • {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'No timestamp'}
                  </span>
                </div>
              </div>
            ))}
            {!logs.length && <div className="empty-cell">No audit log events match these filters.</div>}
          </div>
        )}

        <div className="button-row" style={{ marginTop: '16px' }}>
          <button
            className="ghost-btn small"
            data-testid="audit-log-prev-page-button"
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
          >
            Previous
          </button>
          <button
            className="ghost-btn small"
            data-testid="audit-log-next-page-button"
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total}
          >
            Next
          </button>
        </div>
      </section>
    </div>
  );
}

const PDF_DOC_TYPES = ['audit', 'proposal'];

export function PDFDownloadLog({ api, showNotice }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [docType, setDocType] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (docType) params.doc_type = docType;
      const res = await api.get('/admin/pdf-download-events', { params });
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to load PDF download events', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, showNotice, docType, offset]);

  useEffect(() => { load(); }, [load]);

  const applyDocType = (value) => { setOffset(0); setDocType(value); };

  return (
    <div className="page-stack" data-testid="pdf-download-log">
      <section className="panel glass-panel">
        <div className="panel-header">
          <h3>PDF Download Log</h3>
          <span>{total} downloads recorded</span>
        </div>

        <div className="advanced-filters glass-panel" data-testid="pdf-download-log-filters">
          <select
            data-testid="pdf-download-log-filter-doc-type"
            value={docType}
            onChange={(e) => applyDocType(e.target.value)}
          >
            <option value="">All Document Types</option>
            {PDF_DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="empty-cell">Loading download log...</div>
        ) : (
          <div className="compact-list">
            {events.map((event) => (
              <div key={event.id} className="compact-row">
                <FileText size={16} />
                <div>
                  <strong>{String(event.doc_type || '').toUpperCase()} PDF</strong>
                  <span>
                    {event.requested_by_name || event.requested_by_email || 'Unknown'} ({event.requested_by_type}) • {event.ip || 'no IP'} • {event.created_at ? new Date(event.created_at).toLocaleString() : 'No timestamp'}
                  </span>
                </div>
              </div>
            ))}
            {!events.length && <div className="empty-cell">No PDF downloads recorded yet.</div>}
          </div>
        )}

        <div className="button-row" style={{ marginTop: '16px' }}>
          <button
            className="ghost-btn small"
            data-testid="pdf-download-log-prev-page-button"
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
          >
            Previous
          </button>
          <button
            className="ghost-btn small"
            data-testid="pdf-download-log-next-page-button"
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total}
          >
            Next
          </button>
        </div>
      </section>
    </div>
  );
}

export default {
  TwoFactorEnrollment,
  TwoFactorVerification,
  SecurityDashboard,
  SessionManagement,
  AuditLogPanel,
  PDFDownloadLog
};
